import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, Upload, Plus, Loader2, RefreshCw, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import supabase from '../../lib/supabase';
import { useOrgContext } from '../../context/OrgContext';
import * as XLSX from 'xlsx';
import { extractCoordinatesFromUrl, isShortMapsUrl, optimizeRoute } from '../../lib/mapsUtils';
import { getCityFromCoords } from '../../lib/geoHelper';
import { ContactsDataTable } from '../../components/ContactsDataTable';
import { saveUserPreferences, loadUserPreferences, migrateLocalStorageToDatabase, type ContactsTablePreferences } from '../../lib/userPreferences';
import type { SortingState, ColumnFiltersState } from '@tanstack/react-table';

type ProspectStatus = 'new' | 'to_visit' | 'visited_interested' | 'visited_not_interested' | 'nurture' | 'disqualified' | null;

type Contact = {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  source: string | null;
  last_activity_at: string | null;
  tags: string[] | null;
  created_at?: string;
  lifecycle_stage?: 'contact' | 'lead' | 'client' | null;
  vat_number?: string | null;
  client_rank?: number | null;
  company?: string | null;
  role?: string | null;
  preferred_language?: string | null;
  readonly?: boolean;
  // Outbound prospecting fields
  company_address?: string | null;
  company_city?: string | null;
  company_zip?: string | null;
  google_maps_url?: string | null;
  website?: string | null;
  prospect_status?: ProspectStatus;
  lat?: number | null;
  lng?: number | null;
  interest_level?: 'low' | 'medium' | 'high' | null;
  route_index?: number | null;
  latest_note?: string | null;
  latest_note_id?: string | null;
};

const POPULAR_SOURCE_OPTIONS = [
  'Vector Insider',
  'Website',
  'Referral',
  'LinkedIn',
  'Instagram',
  'Facebook',
  'WhatsApp',
  'Events',
  'Cold Outreach',
  'Paid Ads',
];

export const ContactsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orgId, loading: orgLoading, currentUser } = useOrgContext();
  const canEdit = Boolean(currentUser?.canEdit);
  const [rows, setRows] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [drawer, setDrawer] = useState(false);
  const [newContact, setNewContact] = useState({
    full_name: '',
    email: '',
    phone: '',
    vat_number: '',
    client_rank: '3',
    source: '',
    tags: '',
    stage: 'contact' as 'contact' | 'lead' | 'client',
    company: '',
    company_address: '',
    company_city: '',
    company_zip: '',
    google_maps_url: '',
    website: '',
    role: '',
    preferred_language: '',
    interest_level: '' as '' | 'low' | 'medium' | 'high',
    route_index: null as number | null,
    latest_note: '',
  });
  const [editContact, setEditContact] = useState<Contact | null>(null);
  const [editLeadStatus, setEditLeadStatus] = useState<'new' | 'qualified' | 'scheduled' | 'won' | 'lost' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Contact | null>(null);
  const [selectedSource, setSelectedSource] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagsDropdownOpen, setTagsDropdownOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState<string>('');
  const [selectedProspectStatus, setSelectedProspectStatus] = useState<string>('');
  const [selectedLeadStatus, setSelectedLeadStatus] = useState<string>('');
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [citiesDropdownOpen, setCitiesDropdownOpen] = useState(false);
  const [sortKey, setSortKey] = useState<'recent' | 'name' | 'route_index_asc' | 'route_index_desc'>('recent');
  const [leadCounts, setLeadCounts] = useState<Record<string, number>>({});
  const [leadStages, setLeadStages] = useState<Record<string, 'new' | 'qualified' | 'scheduled' | 'won' | 'lost'>>({});
  const [showImportModal, setShowImportModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [mapsUrlExpanding, setMapsUrlExpanding] = useState(false);
  const [mapsUrlExpanded, setMapsUrlExpanded] = useState(false);
  const [coordinatesMessage, setCoordinatesMessage] = useState<{ type: 'success' | 'error' | null; text: string }>({ type: null, text: '' });
  const [isFixing, setIsFixing] = useState(false);
  const [isUpdatingCities, setIsUpdatingCities] = useState(false);
  const [isOptimizingRoutes, setIsOptimizingRoutes] = useState(false);
  const userId = currentUser?.id;
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [tableSorting, setTableSorting] = useState<SortingState>([]);
  const [tableColumnFilters, setTableColumnFilters] = useState<ColumnFiltersState>([]);
  
  // Column definitions with translations
  const allColumns = useMemo(() => [
    { key: 'name', label: t('crm.contacts.columns.name') },
    { key: 'company', label: t('crm.contacts.columns.company') },
    { key: 'email', label: t('crm.contacts.columns.email') },
    { key: 'phone', label: t('crm.contacts.columns.phone') },
    { key: 'source', label: t('crm.contacts.columns.source') },
    { key: 'stage', label: t('crm.contacts.columns.stage') },
    { key: 'address', label: t('crm.contacts.columns.address') },
    { key: 'city', label: t('crm.contacts.columns.city') },
    { key: 'prospectStatus', label: t('crm.contacts.columns.prospectStatus') },
    { key: 'leadStatus', label: t('crm.contacts.columns.leadStatus') },
    { key: 'tags', label: t('crm.contacts.columns.tags') },
    { key: 'interest_level', label: t('crm.contacts.columns.interest_level') },
    { key: 'route_index', label: t('crm.contacts.columns.route_index') },
    { key: 'latest_note', label: t('crm.contacts.columns.latest_note') },
    { key: 'vat', label: t('crm.contacts.columns.vat') },
    { key: 'rank', label: t('crm.contacts.columns.rank') },
    { key: 'lastActivity', label: t('crm.contacts.columns.lastActivity') },
    { key: 'website', label: t('crm.contacts.columns.website') },
    { key: 'googleMaps', label: t('crm.contacts.columns.googleMaps') },
    { key: 'whatsapp', label: t('crm.contacts.columns.whatsapp') },
  ], [t]);

  // Column order state - will be loaded from database
  const [columnOrder, setColumnOrder] = useState<string[]>(allColumns.map(c => c.key));

  // Column visibility state - will be loaded from database
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
    name: true,
    company: true,
    email: true,
    phone: true,
    whatsapp: true,
    source: true,
    stage: true,
    address: true,
    city: true,
    prospectStatus: true,
    tags: true,
    interest_level: true,
    route_index: false, // Hidden by default
    latest_note: true,
    vat: true,
    rank: true,
    lastActivity: true,
    website: true,
    googleMaps: true,
  });

  // Load preferences from database on mount
  useEffect(() => {
    async function loadPrefs() {
      if (!userId || !orgId || preferencesLoaded) return;
      
      // First, try to migrate localStorage to database if it exists
      const migrationResult = await migrateLocalStorageToDatabase(userId, orgId, 'contacts');
      if (migrationResult.success && migrationResult.migrated) {
        console.log('[Contacts] Migrated localStorage preferences to database');
      }
      
      const result = await loadUserPreferences(userId, orgId, 'contacts');
      if (result.success && result.preferences) {
        const prefs = result.preferences as ContactsTablePreferences;
        
        // Restore column order
        if (prefs.columnOrder && Array.isArray(prefs.columnOrder)) {
          const allKeys = allColumns.map(c => c.key);
          const savedKeys = prefs.columnOrder.filter((k: string) => allKeys.includes(k));
          const missingKeys = allKeys.filter(k => !savedKeys.includes(k));
          setColumnOrder([...savedKeys, ...missingKeys]);
        }
        
        // Restore column visibility
        if (prefs.visibleColumns) {
          setVisibleColumns(prefs.visibleColumns);
        }
        
        // Restore filters
        if (prefs.filters) {
          setSelectedSource(prefs.filters.selectedSource || '');
          setSelectedTags(Array.isArray(prefs.filters.selectedTags) ? prefs.filters.selectedTags : (prefs.filters.selectedTag ? [prefs.filters.selectedTag] : []));
          setSelectedStage(prefs.filters.selectedStage || '');
          setSelectedProspectStatus(prefs.filters.selectedProspectStatus || '');
          setSelectedLeadStatus(prefs.filters.selectedLeadStatus || '');
          setSelectedCities(Array.isArray(prefs.filters.selectedCities) ? prefs.filters.selectedCities : (prefs.filters.selectedCity ? [prefs.filters.selectedCity] : []));
        }
        
        // Restore sorting
        if (prefs.sorting) {
          if (prefs.sorting.sortKey) {
            setSortKey(prefs.sorting.sortKey);
          }
          if (prefs.sorting.columnSorting) {
            setTableSorting(prefs.sorting.columnSorting);
          }
        }
        
        // Restore pagination
        if (prefs.pagination) {
          setCurrentPage(prefs.pagination.currentPage || 1);
        }
        
        // Restore column filters
        if (prefs.columnFilters) {
          setTableColumnFilters(prefs.columnFilters);
        }
      }
      
      setPreferencesLoaded(true);
    }
    
    loadPrefs();
  }, [userId, orgId, preferencesLoaded, allColumns]);

  // Debounced save function
  const savePreferencesDebounced = useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return (prefs: ContactsTablePreferences) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        if (userId && orgId) {
          await saveUserPreferences(userId, orgId, 'contacts', prefs);
        }
      }, 1000); // Save 1 second after last change
    };
  }, [userId, orgId]);

  // Save preferences when they change
  useEffect(() => {
    if (!preferencesLoaded || !userId || !orgId) return;
    
    const prefs: ContactsTablePreferences = {
      columnOrder,
      visibleColumns,
      filters: {
        selectedSource,
        selectedTags: selectedTags,
        selectedStage,
        selectedProspectStatus,
        selectedLeadStatus,
        selectedCities: selectedCities,
      },
      sorting: {
        sortKey,
        columnSorting: tableSorting,
      },
      pagination: {
        currentPage,
        itemsPerPage,
      },
      columnFilters: tableColumnFilters,
    };
    
    savePreferencesDebounced(prefs);
  }, [
    columnOrder,
    visibleColumns,
    selectedSource,
    selectedTags,
    selectedStage,
    selectedProspectStatus,
    selectedLeadStatus,
    selectedCities,
    sortKey,
    currentPage,
    tableSorting,
    tableColumnFilters,
    preferencesLoaded,
    userId,
    orgId,
    savePreferencesDebounced,
  ]);



  const fetchContacts = useCallback(async () => {
    if (!orgId) {
      console.warn('[CRM] No orgId available, cannot load contacts');
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    const loadContacts = async (): Promise<Contact[]> => {
      console.log('[CRM] Loading contacts for orgId:', orgId);
      
      // Try with all columns including company, role, preferred_language, outbound fields, and coordinates, plus activities for notes
      const extendedSelect =
        'id, full_name, email, phone, source, last_activity_at, tags, created_at, lifecycle_stage, vat_number, client_rank, company, role, preferred_language, company_address, company_city, company_zip, google_maps_url, website, prospect_status, lat, lng, interest_level, route_index, activities(id, content, type, created_at)';
      const extendedResp = await supabase
        .from('contacts')
        .select(extendedSelect)
        .eq('org_id', orgId)
        .order('last_activity_at', { ascending: false })
        .limit(1000);

      if (!extendedResp.error && Array.isArray(extendedResp.data)) {
        console.log('[CRM] Loaded', extendedResp.data.length, 'contacts with extended columns');
        return (extendedResp.data as any[]).map((row) => {
          // Extract latest note
          const latestActivity = Array.isArray(row.activities) 
            ? row.activities
                .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                .find((a: any) => a.type === 'note')
            : null;

          return { 
            ...row, 
            route_index: row.route_index != null ? Number(row.route_index) : null,
            latest_note: latestActivity?.content || null,
            latest_note_id: latestActivity?.id || null,
            readonly: false 
          };
        });
      }

      if (extendedResp.error) {
        console.warn('[CRM] Contacts extended columns unavailable, falling back.', extendedResp.error);
        const errorMsg = extendedResp.error.message || '';
        
        // If website, lat, or lng columns are missing, try without them
        if (errorMsg.includes('website') || errorMsg.includes('lat') || errorMsg.includes('lng') || errorMsg.includes('column')) {
          let selectWithoutMissing = extendedSelect;
          if (errorMsg.includes('website')) {
            selectWithoutMissing = selectWithoutMissing.replace(', website', '').replace('website, ', '');
          }
          if (errorMsg.includes('lat')) {
            selectWithoutMissing = selectWithoutMissing.replace(', lat', '').replace('lat, ', '').replace(', lng', '').replace('lng, ', '');
          }
          
          const fallbackResp = await supabase
            .from('contacts')
            .select(selectWithoutMissing)
            .eq('org_id', orgId)
            .order('last_activity_at', { ascending: false })
            .limit(1000);
          
          if (!fallbackResp.error && Array.isArray(fallbackResp.data)) {
            console.log('[CRM] Loaded', fallbackResp.data.length, 'contacts without missing columns');
            return (fallbackResp.data as unknown as Contact[]).map((row) => ({ 
              ...row, 
              website: row.website || null,
              lat: row.lat || null,
              lng: row.lng || null,
              readonly: false 
            }));
          }
        }
      }

      // Fallback without company and role
      const fallbackSelect = 'id, full_name, email, phone, source, last_activity_at, tags, created_at, lifecycle_stage, vat_number, client_rank, lat, lng';
      const fallbackResp = await supabase
        .from('contacts')
        .select(fallbackSelect)
        .eq('org_id', orgId)
        .order('last_activity_at', { ascending: false })
        .limit(1000);
      
      if (!fallbackResp.error && Array.isArray(fallbackResp.data)) {
        console.log('[CRM] Loaded', fallbackResp.data.length, 'contacts with fallback columns');
        return (fallbackResp.data as any[]).map((row) => ({
          ...row,
          vat_number: row.vat_number || null,
          client_rank: row.client_rank || null,
          lat: row.lat || null,
          lng: row.lng || null,
          readonly: false,
        })) as Contact[];
      }

      if (fallbackResp.error) {
        console.error('[CRM] Failed to load contacts.', fallbackResp.error);
      }
      console.warn('[CRM] No contacts loaded, returning empty array');
      return [];
    };

    try {
      const contactsRows = await loadContacts();
      console.log('[CRM] Total contacts loaded:', contactsRows.length);
      setRows(contactsRows);
    } catch (error) {
      console.error('[CRM] Error loading contacts:', error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (!orgId) {
      setRows([]);
      setLoading(orgLoading);
      return;
    }
    fetchContacts();
  }, [orgId, orgLoading, fetchContacts]);

  useEffect(() => {
    if (!orgId) {
      setLeadCounts({});
      setLeadStages({});
      return;
    }
    (async () => {
      // Fetch lead counts for open stages
      const { data: countData, error: countError } = await supabase
        .from('leads')
        .select('id, contact_id')
        .eq('org_id', orgId)
        .in('stage', ['new', 'qualified', 'scheduled']);
      if (!countError && countData) {
        const map: Record<string, number> = {};
        countData.forEach((lead) => {
          if (lead.contact_id) {
            map[lead.contact_id] = (map[lead.contact_id] || 0) + 1;
          }
        });
        setLeadCounts(map);
      }

      // Fetch lead stages for all contacts
      const { data: stageData, error: stageError } = await supabase
        .from('leads')
        .select('contact_id, stage')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false });
      if (!stageError && stageData) {
        const stageMap: Record<string, 'new' | 'qualified' | 'scheduled' | 'won' | 'lost'> = {};
        stageData.forEach((lead) => {
          if (lead.contact_id && lead.stage && !stageMap[lead.contact_id]) {
            // Only take the first (most recent) lead stage for each contact
            stageMap[lead.contact_id] = lead.stage as 'new' | 'qualified' | 'scheduled' | 'won' | 'lost';
          }
        });
        setLeadStages(stageMap);
      }
    })();
  }, [orgId]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    // Start with all contacts - no automatic exclusion. Contacts stay visible when status changes.
    // Stage filter only applies when user explicitly selects a filter.
    let result = [...rows];
    if (term) {
      result = result.filter(r =>
        (r.full_name || '').toLowerCase().includes(term) ||
        (r.email || '').toLowerCase().includes(term) ||
        (r.phone || '').toLowerCase().includes(term) ||
        (r.company || '').toLowerCase().includes(term) ||
        (r.company_address || '').toLowerCase().includes(term)
      );
    }
    if (selectedSource) {
      result = result.filter((r) => (r.source || '').toLowerCase() === selectedSource.toLowerCase());
    }
    if (selectedTags.length > 0) {
      result = result.filter((r) => {
        const firstTag = (r.tags || [])[0];
        if (!firstTag) return false;
        return selectedTags.some(selectedTag => 
          firstTag.toLowerCase() === selectedTag.toLowerCase()
        );
      });
    }
    if (selectedStage) {
      if (selectedStage === 'prospect') {
        result = result.filter((r) => r.prospect_status != null);
      } else if (selectedStage === 'contact') {
        result = result.filter((r) => (r.lifecycle_stage || 'contact') === 'contact' && !r.prospect_status);
      } else if (selectedStage === 'lead') {
        result = result.filter((r) => (r.lifecycle_stage || 'contact') === 'lead');
      } else if (selectedStage === 'client') {
        result = result.filter((r) => (r.lifecycle_stage || 'contact') === 'client');
      }
    }
    if (selectedProspectStatus) {
      result = result.filter((r) => (r.prospect_status || '') === selectedProspectStatus);
    }
    if (selectedLeadStatus) {
      result = result.filter((r) => {
        const contactLeadStatus = leadStages[r.id];
        return contactLeadStatus && contactLeadStatus === selectedLeadStatus;
      });
    }
    if (selectedCities.length > 0) {
      result = result.filter((r) => {
        const city = (r.company_city || '').trim();
        if (!city) return false;
        return selectedCities.some(selectedCity => 
          city.toLowerCase() === selectedCity.toLowerCase()
        );
      });
    }
    if (sortKey === 'recent') {
      result = [...result].sort((a, b) => new Date(b.last_activity_at || b.created_at || 0).getTime() - new Date(a.last_activity_at || a.created_at || 0).getTime());
    } else if (sortKey === 'route_index_asc') {
      result = [...result].sort((a, b) => {
        if (a.route_index === null || a.route_index === undefined) return 1;
        if (b.route_index === null || b.route_index === undefined) return -1;
        return a.route_index - b.route_index;
      });
    } else if (sortKey === 'route_index_desc') {
      result = [...result].sort((a, b) => {
        if (a.route_index === null || a.route_index === undefined) return 1;
        if (b.route_index === null || b.route_index === undefined) return -1;
        return b.route_index - a.route_index;
      });
    } else {
      result = [...result].sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
    }
    return result;
  }, [rows, q, selectedSource, selectedTags, selectedStage, selectedProspectStatus, selectedLeadStatus, selectedCities, sortKey, leadStages]);

  // Pagination state (will be updated by DataTable)
  const [filteredCount, setFilteredCount] = useState(filtered.length);
  
  // Update filteredCount when filtered changes (initial load)
  useEffect(() => {
    setFilteredCount(filtered.length);
  }, [filtered.length]);
  
  // Calculate pagination based on filtered count from DataTable
  const totalPages = Math.ceil(filteredCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  // Reset to page 1 when top-level filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [q, selectedSource, selectedTags, selectedStage, selectedProspectStatus, selectedLeadStatus, selectedCities, sortKey]);

  const sourceOptions = useMemo(() => {
    const unique = new Set<string>(POPULAR_SOURCE_OPTIONS);
    rows.forEach((r) => r.source && unique.add(r.source));
    return Array.from(unique);
  }, [rows]);

  const tags = useMemo(() => {
    const unique = new Set<string>();
    rows.forEach((r) => {
      // Only use the first tag from each contact (business category)
      const firstTag = (r.tags || [])[0];
      if (firstTag) {
        unique.add(firstTag);
      }
    });
    return Array.from(unique).sort();
  }, [rows]);

  const stageOptions = useMemo(
    () => [
      { value: 'contact', label: t('crm.common.stages.contact') },
      { value: 'lead', label: t('crm.common.stages.lead') },
      { value: 'client', label: t('crm.common.stages.client') },
      { value: 'prospect', label: t('crm.contacts.drawer.prospectTypeOption') },
    ],
    [t]
  );

  const prospectStatusOptions = useMemo(
    () => [
      { value: 'new', label: t('crm.prospezione.stages.new') },
      { value: 'to_visit', label: t('crm.prospezione.stages.to_visit') },
      { value: 'visited_interested', label: t('crm.prospezione.stages.visited_interested') },
      { value: 'visited_not_interested', label: t('crm.prospezione.stages.visited_not_interested') },
      { value: 'nurture', label: t('crm.prospezione.stages.nurture') },
      { value: 'disqualified', label: t('crm.prospezione.stages.disqualified') },
    ],
    [t]
  );

  const leadStatusOptions = useMemo(
    () => [
      { value: 'new', label: t('crm.leads.pipeline.headings.new') },
      { value: 'qualified', label: t('crm.leads.pipeline.headings.qualified') },
      { value: 'scheduled', label: t('crm.leads.pipeline.headings.scheduled') },
      { value: 'won', label: t('crm.leads.pipeline.headings.won') },
      { value: 'lost', label: t('crm.leads.pipeline.headings.lost') },
    ],
    [t]
  );

  const cities = useMemo(() => {
    const unique = new Set<string>();
    rows.forEach((r) => {
      const city = r.company_city?.trim();
      if (city) {
        unique.add(city);
      }
    });
    return Array.from(unique).sort();
  }, [rows]);

  const duplicateEmails = useMemo(() => {
    const map = new Map<string, number>();
    rows.forEach((r) => {
      if (r.email) {
        const key = r.email.toLowerCase();
        map.set(key, (map.get(key) || 0) + 1);
      }
    });
    return new Set(Array.from(map.entries()).filter(([, count]) => count > 1).map(([email]) => email));
  }, [rows]);

  const duplicatePhones = useMemo(() => {
    const map = new Map<string, number>();
    rows.forEach((r) => {
      if (r.phone) {
        const key = r.phone.replace(/\D/g, '');
        map.set(key, (map.get(key) || 0) + 1);
      }
    });
    return new Set(Array.from(map.entries()).filter(([, count]) => count > 1).map(([phone]) => phone));
  }, [rows]);

  const formatRelative = useCallback((iso?: string | null) => {
    if (!iso) return '—';
    const diff = Date.now() - new Date(iso).getTime();
    const day = 1000 * 60 * 60 * 24;
    if (diff < day) return t('crm.common.relative.today');
    if (diff < day * 2) return t('crm.common.relative.yesterday');
    const days = Math.round(diff / day);
    return t('crm.common.relative.daysAgo', { count: days });
  }, [t]);

  // Function to find coordinates from Maps URL
  const findCoordinates = useCallback(async (url: string, isEdit: boolean) => {
    if (!url || url.trim().length === 0) {
      setCoordinatesMessage({ type: 'error', text: t('crm.contacts.validMapsUrl') });
      setTimeout(() => setCoordinatesMessage({ type: null, text: '' }), 3000);
      return;
    }

    setMapsUrlExpanding(true);
    setCoordinatesMessage({ type: null, text: '' });

    try {
      // First try to extract coordinates directly (for non-short URLs)
      let coords = extractCoordinatesFromUrl(url);
      let finalUrl = url;

      // If no coordinates found, try expanding the URL (for short URLs)
      if (!coords) {
        const response = await fetch('/api/utils/expand-maps-url', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url })
        });
        const data = await response.json();

        if (data.success && data.lat && data.lng) {
          coords = { lat: data.lat, lng: data.lng };
          finalUrl = data.longUrl || url;
        } else {
          throw new Error(data.error || 'Could not extract coordinates');
        }
      }

      if (coords) {
        // Update form state
        if (isEdit && editContact) {
          setEditContact({
            ...editContact,
            google_maps_url: finalUrl,
            lat: coords.lat,
            lng: coords.lng
          });
          // Save to database
          if (orgId) {
            await supabase
              .from('contacts')
              .update({ 
                lat: coords.lat, 
                lng: coords.lng,
                google_maps_url: finalUrl
              })
              .eq('id', editContact.id)
              .eq('org_id', orgId);
          }
        } else {
          // For new contacts, update form (coordinates will be saved on submit)
          setNewContact({
            ...newContact,
            google_maps_url: finalUrl
          });
        }

        // Show success message
        setCoordinatesMessage({ 
          type: 'success', 
          text: `Posizione trovata: ${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}` 
        });
        setTimeout(() => setCoordinatesMessage({ type: null, text: '' }), 5000);
        setMapsUrlExpanded(true);
      } else {
        throw new Error('Could not extract coordinates');
      }
    } catch (error: any) {
      console.error('[Contacts] Error finding coordinates:', error);
      setCoordinatesMessage({ 
        type: 'error', 
        text: t('crm.contacts.coordinatesExtractError') 
      });
      setTimeout(() => setCoordinatesMessage({ type: null, text: '' }), 5000);
    } finally {
      setMapsUrlExpanding(false);
    }
  }, [editContact, newContact, orgId, t]);

  // Close tags dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tagsDropdownOpen && !(event.target as HTMLElement).closest('.relative')) {
        setTagsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [tagsDropdownOpen]);

  // Close cities dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (citiesDropdownOpen && !(event.target as HTMLElement).closest('.relative')) {
        setCitiesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [citiesDropdownOpen]);

  // Batch coordinate fixer
  const processBatch = async () => {
    if (!orgId) return;
    
    setIsFixing(true);
    let fixedCount = 0;

    // Filter only contacts that need fixing
    const contactsToFix = rows.filter(c => c.google_maps_url && (!c.lat || !c.lng));

    if (contactsToFix.length === 0) {
      toast.success(t('crm.contacts.toasts.noContactsToFix'));
      setIsFixing(false);
      return;
    }

    toast.loading(t('crm.contacts.toasts.processingCount', { count: contactsToFix.length }), { id: 'batch-fix' });

    for (const contact of contactsToFix) {
      try {
        // 1. Call the Extractor API
        const response = await fetch('/api/utils/expand-maps-url', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: contact.google_maps_url }),
        });
        const data = await response.json();

        if (data.success && data.lat && data.lng) {
          // 2. CRITICAL: PATCH THE DATABASE (Supabase)
          const { error } = await supabase
            .from('contacts')
            .update({ 
              lat: data.lat, 
              lng: data.lng,
              // Optional: update the URL to the long version if you want
              // google_maps_url: data.longUrl 
            })
            .eq('id', contact.id)
            .eq('org_id', orgId);

          if (!error) {
            fixedCount++;
            // 3. Update Local State (so UI reflects change instantly)
            setRows(prev => prev.map(c => 
              c.id === contact.id ? { ...c, lat: data.lat, lng: data.lng } : c
            ));
            toast.success(t('crm.contacts.toasts.coordsUpdatedFor', { name: contact.company || contact.full_name || 'Contatto' }), {
              id: `fix-${contact.id}`,
              duration: 2000
            });
          } else {
            console.error(`Failed to update contact ${contact.id}:`, error);
          }
        } else {
          console.warn(`Could not extract coordinates for contact ${contact.id}:`, data.error || 'Unknown error');
          // Log the URL for debugging (truncated)
          if (contact.google_maps_url) {
            console.log(`  URL: ${contact.google_maps_url.substring(0, 100)}...`);
            if (data.longUrl) {
              console.log(`  Expanded URL: ${data.longUrl.substring(0, 100)}...`);
            }
          }
        }
      } catch (err) {
        console.error(`Failed to fix contact ${contact.id}`, err);
      }
      
      // Optional: Small delay to be nice to the server
      await new Promise(r => setTimeout(r, 500));
    }

    setIsFixing(false);
    toast.success(t('crm.contacts.toasts.batchCompleteCoords', { count: fixedCount }), {
      id: 'batch-fix',
      duration: 5000
    });

    // Refresh contacts to ensure everything is in sync
    await fetchContacts();
  };

  // Batch city updater - converts coordinates to city names
  const processCityBatch = async () => {
    if (!orgId) return;
    
    setIsUpdatingCities(true);
    let updatedCount = 0;

    // Filter only contacts that have coordinates but no city
    const contactsToUpdate = rows.filter(c => 
      c.lat != null && c.lng != null && (!c.company_city || c.company_city.trim() === '')
    );

    if (contactsToUpdate.length === 0) {
      toast.success(t('crm.contacts.toasts.noContactsToUpdate'));
      setIsUpdatingCities(false);
      return;
    }

    toast.loading(t('crm.contacts.toasts.processingCount', { count: contactsToUpdate.length }), { id: 'batch-city' });

    for (const contact of contactsToUpdate) {
      try {
        if (contact.lat != null && contact.lng != null) {
          // Get city from coordinates via API
          const cityInfo = await getCityFromCoords(contact.lat, contact.lng);

          if (cityInfo) {
            // Update database with city name
            const { error } = await supabase
              .from('contacts')
              .update({ 
                company_city: cityInfo.city,
              })
              .eq('id', contact.id)
              .eq('org_id', orgId);

            if (!error) {
              updatedCount++;
              // Update local state
              setRows(prev => prev.map(c => 
                c.id === contact.id ? { ...c, company_city: cityInfo.city } : c
              ));
              toast.success(t('crm.contacts.toasts.cityUpdatedFor', { name: contact.company || contact.full_name || 'Contatto', city: cityInfo.city }), {
                id: `city-${contact.id}`,
                duration: 2000
              });
            } else {
              console.error(`Failed to update city for contact ${contact.id}:`, error);
            }
          } else {
            console.warn(`Could not find city for coordinates: ${contact.lat}, ${contact.lng}`);
          }
        }
      } catch (err) {
        console.error(`Failed to update city for contact ${contact.id}`, err);
      }
      
      // Small delay to avoid overwhelming the system
      await new Promise(r => setTimeout(r, 100));
    }

    setIsUpdatingCities(false);
    toast.success(t('crm.contacts.toasts.batchCompleteCity', { count: updatedCount }), {
      id: 'batch-city',
      duration: 5000
    });

    // Refresh contacts to ensure everything is in sync
    await fetchContacts();
  };

  // Calculate how many contacts need fixing
  const contactsNeedingFix = useMemo(() => {
    return rows.filter(c => c.google_maps_url && (!c.lat || !c.lng)).length;
  }, [rows]);

  // Calculate how many contacts need city update
  const contactsNeedingCityUpdate = useMemo(() => {
    return rows.filter(c => 
      c.lat != null && c.lng != null && (!c.company_city || c.company_city.trim() === '')
    ).length;
  }, [rows]);

  // Batch route optimizer - calculates optimal route and assigns route_index
  const processRouteOptimization = async () => {
    if (!orgId) return;
    
    setIsOptimizingRoutes(true);
    let optimizedCount = 0;

    // Filter only contacts that have coordinates
    const contactsWithCoords = rows.filter(c => 
      c.lat != null && c.lng != null
    );

    if (contactsWithCoords.length === 0) {
      toast.success(t('crm.contacts.toasts.noContactsForRoute'));
      setIsOptimizingRoutes(false);
      return;
    }

    toast.loading(t('crm.contacts.toasts.optimizingRoute', { count: contactsWithCoords.length }), { id: 'batch-route' });

    try {
      // Get user's current location as starting point (or use first contact)
      let startPoint: [number, number];
      
      try {
        // Try to get user's current location
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 5000,
            maximumAge: 60000
          });
        });
        startPoint = [position.coords.latitude, position.coords.longitude];
        console.log('[RouteOptimization] Using user location as start point:', startPoint);
      } catch (error) {
        // Fallback: use first contact as starting point
        console.warn('[RouteOptimization] Using first contact as start point');
        const firstContact = contactsWithCoords[0];
        if (firstContact.lat && firstContact.lng) {
          startPoint = [firstContact.lat, firstContact.lng];
        } else {
          toast.error(t('crm.contacts.toasts.cannotDetermineStart'));
          setIsOptimizingRoutes(false);
          return;
        }
      }

      // Optimize route using Nearest Neighbor algorithm
      const optimized = optimizeRoute(
        startPoint,
        contactsWithCoords.map(c => ({ 
          ...c, 
          lat: c.lat ?? null, 
          lng: c.lng ?? null 
        }))
      );

      // Assign route_index (1, 2, 3, ...) to each contact in optimized order
      const updates: Array<{ id: string; route_index: number }> = optimized.map((contact, index) => ({
        id: contact.id,
        route_index: index + 1
      }));

      // Update database in batches to avoid overwhelming it
      const batchSize = 50;
      for (let i = 0; i < updates.length; i += batchSize) {
        const batch = updates.slice(i, i + batchSize);
        
        // Update each contact in the batch
        for (const update of batch) {
          const { error } = await supabase
            .from('contacts')
            .update({ route_index: update.route_index })
            .eq('id', update.id)
            .eq('org_id', orgId);

          if (!error) {
            optimizedCount++;
            // Update local state
            setRows(prev => prev.map(c => 
              c.id === update.id ? { ...c, route_index: update.route_index } : c
            ));
          } else {
            console.error(`Failed to update route_index for contact ${update.id}:`, error);
          }
        }

        // Small delay between batches
        if (i + batchSize < updates.length) {
          await new Promise(r => setTimeout(r, 100));
        }
      }

      setIsOptimizingRoutes(false);
      toast.success(t('crm.contacts.toasts.routeOptimized', { count: optimizedCount }), {
        id: 'batch-route',
        duration: 5000
      });

      // Refresh contacts to ensure everything is in sync
      await fetchContacts();
    } catch (error: any) {
      console.error('[RouteOptimization] Error:', error);
      setIsOptimizingRoutes(false);
      toast.error(t('crm.contacts.toasts.routeError', { message: error.message }), {
        id: 'batch-route',
        duration: 5000
      });
    }
  };

  // Calculate how many contacts have coordinates and could be optimized
  const contactsWithCoordinates = useMemo(() => {
    return rows.filter(c => c.lat != null && c.lng != null).length;
  }, [rows]);

  async function createContact() {
    if (!canEdit) return;
    if (!orgId) return;
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth.user?.id;
    
    // Extract coordinates from Google Maps URL if present
    let lat: number | null = null;
    let lng: number | null = null;
    if (newContact.google_maps_url) {
      const coords = extractCoordinatesFromUrl(newContact.google_maps_url);
      if (coords) {
        lat = coords.lat;
        lng = coords.lng;
      }
    }
    
    const payload: any = {
      full_name: newContact.full_name || null,
      email: newContact.email || null,
      phone: newContact.phone || null,
      vat_number: newContact.vat_number || null,
      client_rank: newContact.client_rank ? Number(newContact.client_rank) : null,
      source: newContact.source || 'website',
      created_by: uid || null,
      org_id: orgId,
      lifecycle_stage: newContact.stage || 'contact',
      last_activity_at: new Date().toISOString(),
      company: newContact.company || null,
      company_address: newContact.company_address || null,
      company_city: newContact.company_city || null,
      company_zip: newContact.company_zip || null,
      google_maps_url: newContact.google_maps_url || null,
      website: newContact.website || null,
      role: newContact.role || null,
      preferred_language: newContact.preferred_language || null,
      interest_level: newContact.interest_level || null,
    };
    
    // Add coordinates if extracted
    if (lat != null && lng != null) {
      payload.lat = lat;
      payload.lng = lng;
    }
    
    if (newContact.tags) {
      payload.tags = newContact.tags.split(',').map(s => s.trim()).filter(Boolean);
    }
    let { error } = await supabase.from('contacts').insert([payload]);
    if (error && error.message.includes('website')) {
      // Website column doesn't exist, remove it and try again
      const { website, ...payloadWithoutWebsite } = payload;
      const retryResult = await supabase.from('contacts').insert([payloadWithoutWebsite]);
      if (retryResult.error) {
        alert(t('crm.contacts.importModal.importErrorWebsite', { message: retryResult.error.message }));
        return;
      }
      alert(t('crm.contacts.importModal.contactCreatedWithoutWebsite'));
    } else if (error) {
      alert(t('crm.contacts.importModal.importError', { message: error.message }));
      return;
    }
    setDrawer(false);
    setNewContact({ full_name: '', email: '', phone: '', vat_number: '', client_rank: '3', source: '', tags: '', stage: 'contact', company: '', company_address: '', company_city: '', company_zip: '', google_maps_url: '', website: '', role: '', preferred_language: '', interest_level: '' as '' | 'low' | 'medium' | 'high', route_index: null, latest_note: '' });
    await fetchContacts();
  }

  async function updateContact(contactId: string) {
    if (!canEdit) return;
    if (!orgId || !editContact) return;
    
    const isProspect = Boolean(editContact.prospect_status);
    const isLead = editContact.lifecycle_stage === 'lead' && !isProspect;
    
    // Update contact
    const updatePayload: any = {
      full_name: editContact.full_name || null,
      email: editContact.email || null,
      phone: editContact.phone || null,
      source: editContact.source || null,
      tags: editContact.tags || null,
      vat_number: editContact.vat_number || null,
      client_rank: editContact.client_rank || null,
      lifecycle_stage: isProspect ? 'contact' : (editContact.lifecycle_stage || 'contact'),
      company: editContact.company || null,
      company_address: editContact.company_address || null,
      company_city: editContact.company_city || null,
      company_zip: editContact.company_zip || null,
      google_maps_url: editContact.google_maps_url || null,
      website: editContact.website || null,
      role: editContact.role || null,
      preferred_language: editContact.preferred_language || null,
      prospect_status: editContact.prospect_status || null,
      interest_level: editContact.interest_level || null,
      route_index: editContact.route_index || null,
    };
    
    let { error } = await supabase
      .from('contacts')
      .update(updatePayload)
      .eq('id', contactId)
      .eq('org_id', orgId);
    
    if (error && error.message.includes('website')) {
      // Website column doesn't exist, remove it and try again
      const { website, ...updateWithoutWebsite } = updatePayload;
      const retryResult = await supabase
        .from('contacts')
        .update(updateWithoutWebsite)
        .eq('id', contactId)
        .eq('org_id', orgId);
      
      if (retryResult.error) {
        alert(t('crm.contacts.importModal.importErrorWebsite', { message: retryResult.error.message }));
        return;
      }
      alert(t('crm.contacts.importModal.contactUpdatedWithoutWebsite'));
    } else if (error) {
      alert(t('crm.contacts.importModal.importError', { message: error.message }));
      return;
    }

    // Handle Note Update
    if (editContact.latest_note !== undefined) {
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth.user?.id;
      
      if (editContact.latest_note_id) {
        // Update existing note
        if (editContact.latest_note) {
          await supabase
            .from('activities')
            .update({ content: editContact.latest_note })
            .eq('id', editContact.latest_note_id)
            .eq('org_id', orgId);
        } else {
          // If empty, maybe delete? Or just clear content?
          // Usually better to keep record or delete if explicitly asked.
          // For now, just update to empty string or ignore if you don't want to delete
          await supabase
            .from('activities')
            .update({ content: '' })
            .eq('id', editContact.latest_note_id)
            .eq('org_id', orgId);
        }
      } else if (editContact.latest_note && editContact.latest_note.trim()) {
        // Create new note if there wasn't one and we have content
        await supabase.from('activities').insert([{
          contact_id: contactId,
          org_id: orgId,
          type: 'note',
          channel: 'web',
          content: editContact.latest_note,
          created_by: uid
        }]);
      }
    }
    
    // If Lead is selected, create or update a lead
    if (isLead) {
      // Get the lead stage from editLeadStatus, default to 'new' if not set
      const leadStage = editLeadStatus || 'new';
      
      // Check if lead already exists
      const { data: existingLead } = await supabase
        .from('leads')
        .select('id, stage')
        .eq('contact_id', contactId)
        .eq('org_id', orgId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (!existingLead) {
        // Create new lead with the selected stage
        const leadTitle = editContact.company || editContact.full_name || 'Nuovo Lead';
        const leadPayload = {
          contact_id: contactId,
          title: `Lead: ${leadTitle}`,
          stage: leadStage as 'new' | 'qualified' | 'scheduled' | 'won' | 'lost',
          org_id: orgId,
        };
        console.log('[Contacts] Creating lead with payload:', leadPayload);
        
        const { data: newLead, error: leadError } = await supabase
          .from('leads')
          .insert([leadPayload])
          .select('id, stage, title, contact_id, org_id')
          .single();
        
        if (leadError) {
          console.error('[Contacts] Error creating lead:', leadError);
          alert(t('crm.leads.createError', { message: leadError.message }));
          return;
        }
        console.log('[Contacts] Lead created successfully:', newLead);
        
        // Verify the lead was created with correct stage
        if (newLead && newLead.stage !== leadStage) {
          console.warn('[Contacts] Lead created but stage mismatch:', newLead.stage, 'Expected:', leadStage, 'Updating...');
          await supabase
            .from('leads')
            .update({ stage: leadStage })
            .eq('id', newLead.id)
            .eq('org_id', orgId);
        }
      } else {
        console.log('[Contacts] Lead already exists for this contact:', existingLead.id);
        // Update existing lead's stage if it's different
        if (existingLead.stage !== leadStage) {
          console.log('[Contacts] Updating existing lead stage to:', leadStage, 'from', existingLead.stage);
          const { error: updateError } = await supabase
            .from('leads')
            .update({ stage: leadStage as 'new' | 'qualified' | 'scheduled' | 'won' | 'lost' })
            .eq('id', existingLead.id)
            .eq('org_id', orgId);
          
          if (updateError) {
            console.error('[Contacts] Error updating lead stage:', updateError);
          }
        }
      }
    }
    
    setEditContact(null);
    setEditLeadStatus(null);
    setDrawer(false);
    await fetchContacts();
    
    // Navigation disabled - user stays on current screen
  }

  async function deleteContact(contactId: string) {
    if (!canEdit) return;
    if (!orgId) return;
    const { error } = await supabase
      .from('contacts')
      .delete()
      .eq('id', contactId)
      .eq('org_id', orgId);
    if (error) {
      alert(t('crm.contacts.delete.error', { message: error.message }));
      return;
    }
    setConfirmDelete(null);
    await fetchContacts();
  }

  async function handleFileImport(file: File) {
    if (!canEdit || !orgId) return;
    
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth.user?.id;
    const contactsToInsert: any[] = [];

    try {
      // Check if it's Excel file
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        // Handle Excel file
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        
        // Try to read from "Recovered_Sheet1" sheet, fallback to first sheet
        let worksheet = workbook.Sheets['Recovered_Sheet1'] || workbook.Sheets[workbook.SheetNames[0]];
        
        if (!worksheet) {
          alert(t('crm.contacts.importModal.sheetNotFound'));
          return;
        }

        // Convert to JSON
        const data = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' }) as any[][];
        
        if (data.length < 2) {
          alert(t('crm.contacts.importModal.excelNeedsHeader'));
          return;
        }

        // Get headers (first row) - keep original case for exact matching
        const headersOriginal = data[0].map((h: any) => String(h || '').trim());
        const headers = headersOriginal.map((h: string) => h.toLowerCase());
        
        // Find column indices - exact matching for the expected columns
        const findColumnIndex = (patterns: string[]) => {
          return headers.findIndex((h: string) => 
            patterns.some(pattern => h === pattern || h.includes(pattern))
          );
        };

        // Map exact column names from the user's file
        const mapsIdx = findColumnIndex(['google maps', 'maps', 'google']);
        const companyIdx = findColumnIndex(['ragione sociale', 'ragione sociale', 'company', 'azienda', 'nome attività']);
        const ratingIdx = findColumnIndex(['rating']);
        const ratingNumIdx = findColumnIndex(['rating#', 'rating #']);
        const categoryIdx = findColumnIndex(['categoria business', 'categoria', 'category']);
        const addressIdx = findColumnIndex(['indirizzo', 'address']);
        const hoursIdx = findColumnIndex(['orario', 'hours', 'orari']);
        const phoneIdx = findColumnIndex(['telefono', 'phone', 'tel']);
        const websiteIdx = findColumnIndex(['website', 'sito', 'url']);

        // Also check for city and zip in case they're in the address or separate
        const cityIdx = findColumnIndex(['città', 'citta', 'city', 'comune']);
        const zipIdx = findColumnIndex(['cap', 'zip', 'postal', 'codice postale']);

        if (companyIdx === -1) {
          alert(t('crm.contacts.importModal.excelNeedsCompanyColumn', { columns: headersOriginal.join(', ') }));
          return;
        }

        // Process data rows
        for (let i = 1; i < data.length; i++) {
          const row = data[i];
          if (!row || row.length === 0) continue;

          const getValue = (idx: number) => idx >= 0 && idx < row.length ? String(row[idx] || '').trim() : null;
          
          const company = getValue(companyIdx);
          if (!company) continue;

          // Get all values
          const rating = getValue(ratingIdx);
          const ratingNum = getValue(ratingNumIdx);
          const category = getValue(categoryIdx);
          const hours = getValue(hoursIdx);
          const website = getValue(websiteIdx);
          
          // Parse address to extract city and zip if possible
          const address = getValue(addressIdx);
          let city = getValue(cityIdx);
          let zip = getValue(zipIdx);
          
          // Try to extract city and zip from address if not in separate columns
          if (address && !city && !zip) {
            const addressMatch = address.match(/(\d{5})\s+(.+)/);
            if (addressMatch) {
              zip = addressMatch[1];
              city = addressMatch[2];
            }
          }

          // Build tags array with category, rating, and other info (but NOT website - it has its own column)
          const tags: string[] = [];
          if (category) tags.push(category);
          if (rating) tags.push(`Rating: ${rating}`);
          if (ratingNum) tags.push(`Rating#: ${ratingNum}`);
          if (hours) tags.push(`Orario: ${hours}`);

          contactsToInsert.push({
            company: company,
            company_address: address,
            company_city: city,
            company_zip: zip,
            google_maps_url: getValue(mapsIdx),
            phone: getValue(phoneIdx),
            website: website, // Save website in separate column
            tags: tags.length > 0 ? tags : null,
            prospect_status: 'new',
            source: 'cold_outreach',
            org_id: orgId,
            created_by: uid || null,
            lifecycle_stage: 'contact',
            last_activity_at: new Date().toISOString(),
          });
        }
      } else {
        // Handle CSV file (original logic)
        const text = await file.text();
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) {
          alert(t('crm.contacts.importModal.csvNeedsHeader'));
          return;
        }

        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const nameIdx = headers.findIndex(h => h.includes('nome') || h.includes('name') || h.includes('attività'));
        const addressIdx = headers.findIndex(h => h.includes('indirizzo') || h.includes('address'));
        const cityIdx = headers.findIndex(h => h.includes('città') || h.includes('city') || h.includes('citta'));
        const zipIdx = headers.findIndex(h => h.includes('cap') || h.includes('zip') || h.includes('postal'));
        const mapsIdx = headers.findIndex(h => h.includes('maps') || h.includes('google') || h.includes('url'));

        if (nameIdx === -1) {
          alert(t('crm.contacts.importModal.csvNeedsNameColumn'));
          return;
        }

        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
          const company = values[nameIdx] || null;
          if (!company) continue;

          contactsToInsert.push({
            company: company,
            company_address: addressIdx >= 0 ? values[addressIdx] || null : null,
            company_city: cityIdx >= 0 ? values[cityIdx] || null : null,
            company_zip: zipIdx >= 0 ? values[zipIdx] || null : null,
            google_maps_url: mapsIdx >= 0 ? values[mapsIdx] || null : null,
            prospect_status: 'new',
            source: 'cold_outreach',
            org_id: orgId,
            created_by: uid || null,
            lifecycle_stage: 'contact',
            last_activity_at: new Date().toISOString(),
          });
        }
      }

      if (contactsToInsert.length === 0) {
        alert(t('crm.contacts.importModal.noValidContacts'));
        return;
      }

      // Try to insert, but if website column doesn't exist, remove it and try again
      let { error } = await supabase.from('contacts').insert(contactsToInsert);
      if (error && error.message.includes('website')) {
        // Website column doesn't exist, remove it from all contacts and try again
        console.warn('[Import] Website column not found, removing website field from import');
        const contactsWithoutWebsite = contactsToInsert.map(({ website, ...rest }) => rest);
        const retryResult = await supabase.from('contacts').insert(contactsWithoutWebsite);
        if (retryResult.error) {
          alert(t('crm.contacts.importModal.importErrorWebsite', { message: retryResult.error.message }));
          return;
        }
        alert(t('crm.contacts.importModal.importedWithoutWebsite', { count: contactsWithoutWebsite.length }));
        setShowImportModal(false);
        await fetchContacts();
        return;
      }
      
      if (error) {
        alert(t('crm.contacts.importModal.importError', { message: error.message }));
        return;
      }

      alert(t('crm.contacts.importModal.importedCount', { count: contactsToInsert.length }));
      setShowImportModal(false);
      await fetchContacts();
    } catch (error: any) {
      console.error('Import error:', error);
      alert(t('crm.contacts.importModal.importError', { message: error.message || 'Errore sconosciuto' }));
    }
  }

  return (
    <div className="space-y-4 lg:space-y-6 w-full max-w-full" style={{ maxWidth: '100%', width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
      {/* Header with Title and Primary Action */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex-1">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">{t('crm.contacts.title')}</h1>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">{t('crm.contacts.subtitle')}</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowImportModal(true)}
              className="px-4 sm:px-3 py-3 sm:py-2 rounded-xl md:rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white font-bold md:font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 active:scale-[0.98] transition-transform flex items-center justify-center gap-2 text-sm md:text-xs shadow-sm min-h-[48px] md:min-h-0"
            >
              <Upload className="w-5 h-5 md:w-4 md:h-4" />
              <span className="hidden sm:inline">{t('crm.contacts.import')}</span>
              <span className="sm:hidden">{t('crm.contacts.import')}</span>
            </button>
            {contactsNeedingFix > 0 && (
              <button
                onClick={processBatch}
                disabled={isFixing}
                className="px-4 sm:px-3 py-3 sm:py-2 rounded-xl md:rounded-lg bg-amber-500 dark:bg-amber-600 text-white font-bold md:font-semibold hover:bg-amber-600 dark:hover:bg-amber-700 active:scale-[0.98] transition-transform flex items-center justify-center gap-2 text-sm md:text-xs shadow-sm disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px] md:min-h-0"
                title={t('crm.contacts.findCoordinatesFor', { count: contactsNeedingFix })}
              >
                {isFixing ? (
                  <Loader2 className="w-5 h-5 md:w-4 md:h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-5 h-5 md:w-4 md:h-4" />
                )}
                <span className="hidden sm:inline">
                  {isFixing ? t('crm.contacts.fixing') : `${t('crm.contacts.findCoordinates')} (${contactsNeedingFix})`}
                </span>
                {!isFixing && <span className="sm:hidden">{contactsNeedingFix}</span>}
              </button>
            )}
            {contactsNeedingCityUpdate > 0 && (
              <button
                onClick={processCityBatch}
                disabled={isUpdatingCities}
                className="px-4 sm:px-3 py-3 sm:py-2 rounded-xl md:rounded-lg bg-blue-500 dark:bg-blue-600 text-white font-bold md:font-semibold hover:bg-blue-600 dark:hover:bg-blue-700 active:scale-[0.98] transition-transform flex items-center justify-center gap-2 text-sm md:text-xs shadow-sm disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px] md:min-h-0"
                title={t('crm.contacts.updateCityFor', { count: contactsNeedingCityUpdate })}
              >
                {isUpdatingCities ? (
                  <Loader2 className="w-5 h-5 md:w-4 md:h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-5 h-5 md:w-4 md:h-4" />
                )}
                <span className="hidden sm:inline">
                  {isUpdatingCities ? t('crm.contacts.updating') : `${t('crm.contacts.updateCity')} (${contactsNeedingCityUpdate})`}
                </span>
                {!isUpdatingCities && <span className="sm:hidden">{contactsNeedingCityUpdate}</span>}
              </button>
            )}
            {contactsWithCoordinates > 0 && (
              <button
                onClick={processRouteOptimization}
                disabled={isOptimizingRoutes}
                className="px-4 sm:px-3 py-3 sm:py-2 rounded-xl md:rounded-lg bg-purple-500 dark:bg-purple-600 text-white font-bold md:font-semibold hover:bg-purple-600 dark:hover:bg-purple-700 active:scale-[0.98] transition-transform flex items-center justify-center gap-2 text-sm md:text-xs shadow-sm disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px] md:min-h-0"
                title={t('crm.contacts.optimizeRouteFor', { count: contactsWithCoordinates })}
              >
                {isOptimizingRoutes ? (
                  <Loader2 className="w-5 h-5 md:w-4 md:h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-5 h-5 md:w-4 md:h-4" />
                )}
                <span className="hidden sm:inline">
                  {isOptimizingRoutes ? t('crm.contacts.optimizing') : `${t('crm.contacts.optimizeRoute')} (${contactsWithCoordinates})`}
                </span>
                {!isOptimizingRoutes && <span className="sm:hidden">{contactsWithCoordinates}</span>}
              </button>
            )}
            <button
              onClick={() => {
                setEditContact(null);
                setEditLeadStatus(null);
                setNewContact({ full_name: '', email: '', phone: '', vat_number: '', client_rank: '3', source: '', tags: '', stage: 'contact', company: '', company_address: '', company_city: '', company_zip: '', google_maps_url: '', website: '', role: '', preferred_language: '', interest_level: '' as '' | 'low' | 'medium' | 'high', route_index: null, latest_note: '' });
                setDrawer(true);
              }}
              className="px-5 sm:px-4 py-3.5 sm:py-2.5 rounded-xl bg-cyan-500 text-white font-bold md:font-semibold hover:bg-cyan-600 active:scale-[0.98] transition-transform flex items-center justify-center gap-2 text-base md:text-sm shadow-lg hover:shadow-xl min-h-[56px] md:min-h-0"
            >
              <Plus className="w-6 h-6 md:w-5 md:h-5" />
              <span>{t('crm.contacts.newButton')}</span>
            </button>
          </div>
        )}
      </div>

      {/* Filters Section */}
      <div className="flex flex-col gap-3">
        {/* Primary Search Bar - Mobile Optimized */}
        <div className="relative">
          <input
            placeholder={t('crm.common.searchPlaceholder')}
            className="w-full bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3.5 pl-11 text-base md:text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 placeholder-gray-500 dark:placeholder-gray-400 min-h-[48px]"
            value={q}
            onChange={e => setQ(e.target.value)}
          />
          <Filter className="w-5 h-5 md:w-4 md:h-4 text-gray-400 dark:text-gray-500 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>

        {/* Secondary Filters - Grouped (Mobile Optimized) */}
        <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
          <select
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
            className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm md:text-xs text-gray-900 dark:text-white flex-1 sm:flex-initial min-h-[48px] font-medium"
          >
            <option value="">{t('crm.common.filterSource')}</option>
            {sourceOptions.map((src) => (
              <option key={src} value={src}>
                {src}
              </option>
            ))}
          </select>
          <div className="relative flex-1 sm:flex-initial sm:min-w-[200px]">
            <button
              type="button"
              onClick={() => setTagsDropdownOpen(!tagsDropdownOpen)}
              className="w-full bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm md:text-xs text-gray-900 dark:text-white text-left flex items-center justify-between min-h-[48px] font-medium"
            >
              <span className="truncate">
                {selectedTags.length === 0
                  ? t('crm.common.filterTag')
                  : selectedTags.length === 1
                  ? selectedTags[0]
                  : t('crm.common.selected', { count: selectedTags.length })}
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform flex-shrink-0 ${tagsDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {tagsDropdownOpen && (
              <div className="absolute z-50 mt-1 w-full min-w-[250px] max-h-64 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl">
                {tags.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{t('crm.contacts.noTagsAvailable')}</div>
                ) : (
                  <>
                    {tags.map((tag) => {
                      const isSelected = selectedTags.includes(tag);
                      return (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => {
                            if (isSelected) {
                              setSelectedTags(selectedTags.filter(t => t !== tag));
                            } else {
                              setSelectedTags([...selectedTags, tag]);
                            }
                          }}
                          className={`w-full text-left px-3 py-2 text-xs sm:text-sm transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0 flex items-center gap-2 ${
                            isSelected
                              ? 'bg-cyan-50 dark:bg-cyan-900/30 hover:bg-cyan-100 dark:hover:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="flex-1">{tag}</span>
                        </button>
                      );
                    })}
                    {selectedTags.length > 0 && (
                      <div className="border-t border-gray-200 dark:border-gray-700 p-2">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedTags([]);
                            setTagsDropdownOpen(false);
                          }}
                          className="w-full px-3 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        >
                          Cancella tutti
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <select
            value={selectedStage}
            onChange={(e) => setSelectedStage(e.target.value)}
            className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm md:text-xs text-gray-900 dark:text-white flex-1 sm:flex-initial min-h-[48px] font-medium"
          >
            <option value="">{t('crm.common.filterStage')}</option>
            {stageOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={selectedProspectStatus}
            onChange={(e) => setSelectedProspectStatus(e.target.value)}
            className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm md:text-xs text-gray-900 dark:text-white flex-1 sm:flex-initial min-h-[48px] font-medium"
          >
            <option value="">{t('crm.contacts.columns.prospectStatus')}</option>
            {prospectStatusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={selectedLeadStatus}
            onChange={(e) => setSelectedLeadStatus(e.target.value)}
            className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm md:text-xs text-gray-900 dark:text-white flex-1 sm:flex-initial min-h-[48px] font-medium"
          >
            <option value="">{t('crm.contacts.columns.leadStatus')}</option>
            {leadStatusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {cities.length > 0 && (
            <div className="relative flex-1 sm:flex-initial">
              <button
                type="button"
                onClick={() => setCitiesDropdownOpen(!citiesDropdownOpen)}
                className="w-full bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm md:text-xs text-gray-900 dark:text-white text-left flex items-center justify-between min-h-[48px] font-medium"
              >
                <span className="truncate">
                  {selectedCities.length === 0
                    ? 'Città'
                    : selectedCities.length === 1
                    ? selectedCities[0]
                    : `${selectedCities.length} città selezionate`}
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform flex-shrink-0 ${citiesDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {citiesDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full min-w-[250px] max-h-64 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl">
                  {cities.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{t('crm.contacts.noCitiesAvailable')}</div>
                  ) : (
                    <>
                      {cities.map((city) => {
                        const isSelected = selectedCities.includes(city);
                        return (
                          <button
                            key={city}
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                setSelectedCities(selectedCities.filter(c => c !== city));
                              } else {
                                setSelectedCities([...selectedCities, city]);
                              }
                            }}
                            className={`w-full text-left px-3 py-2 text-xs sm:text-sm transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0 flex items-center gap-2 ${
                              isSelected
                                ? 'bg-cyan-50 dark:bg-cyan-900/30 hover:bg-cyan-100 dark:hover:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}}
                              className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="flex-1">{city}</span>
                          </button>
                        );
                      })}
                      {selectedCities.length > 0 && (
                        <div className="border-t border-gray-200 dark:border-gray-700 p-2">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedCities([]);
                              setCitiesDropdownOpen(false);
                            }}
                            className="w-full px-3 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                          >
                            Cancella tutti
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          <select
            value={sortKey}
            onChange={(e) => {
              setSortKey(e.target.value as 'recent' | 'name' | 'route_index_asc' | 'route_index_desc');
              setTableSorting([]); // Clear table sorting to allow mobile sort to take precedence
            }}
            className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm md:text-xs text-gray-900 dark:text-white flex-1 sm:flex-initial min-h-[48px] font-medium"
          >
            <option value="recent">{t('crm.common.sortRecent')}</option>
            <option value="name">{t('crm.common.sortName')}</option>
            <option value="route_index_asc">{t('crm.contacts.routeIndexAsc')}</option>
            <option value="route_index_desc">{t('crm.contacts.routeIndexDesc')}</option>
          </select>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">
          {t('crm.contacts.summary', {
            count: filtered.length,
            duplicateEmails: duplicateEmails.size,
            duplicatePhones: duplicatePhones.size,
          })}
        </div>
      </div>

      {loading ? (
        <div className="text-center p-6 text-gray-500">{t('crm.common.loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-12 text-center shadow-md">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
              <Plus className="w-8 h-8 text-cyan-600 dark:text-cyan-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {q || selectedSource || selectedTags.length > 0 || selectedStage || selectedProspectStatus || selectedLeadStatus || selectedCities.length > 0
                ? t('crm.contacts.empty.noResults')
                : t('crm.contacts.empty.noContactsYet')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              {q || selectedSource || selectedTags.length > 0 || selectedStage || selectedProspectStatus || selectedLeadStatus || selectedCities.length > 0
                ? t('crm.contacts.empty.tryFilters')
                : t('crm.contacts.empty.addFirst')}
            </p>
            {canEdit && (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  onClick={() => {
                    setEditContact(null);
                    setEditLeadStatus(null);
                    setNewContact({ full_name: '', email: '', phone: '', vat_number: '', client_rank: '3', source: '', tags: '', stage: 'contact', company: '', company_address: '', company_city: '', company_zip: '', google_maps_url: '', website: '', role: '', preferred_language: '', interest_level: '' as '' | 'low' | 'medium' | 'high', route_index: null, latest_note: '' });
                    setDrawer(true);
                  }}
                  className="px-5 py-2.5 rounded-lg bg-cyan-500 text-white font-semibold hover:bg-cyan-600 transition-colors flex items-center gap-2 shadow-md hover:shadow-lg"
                >
                  <Plus className="w-5 h-5" />
                  <span>{t('crm.contacts.newButton')}</span>
                </button>
                <button
                  onClick={() => setShowImportModal(true)}
                  className="px-5 py-2.5 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
                >
                  <Upload className="w-5 h-5" />
                  <span>{t('crm.contacts.importList')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <ContactsDataTable
          data={filtered}
          columns={allColumns}
          columnOrder={columnOrder}
          visibleColumns={visibleColumns}
          onColumnOrderChange={setColumnOrder}
          onVisibleColumnsChange={setVisibleColumns}
          onSortingChange={setTableSorting}
          onColumnFiltersChange={setTableColumnFilters}
          initialSorting={tableSorting}
          initialColumnFilters={tableColumnFilters}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onFilteredCountChange={setFilteredCount}
          onUpdateContact={async (contactId, field, value, additionalFields?: Record<string, any>) => {
            if (!canEdit || !orgId) return;
            const prevContact = rows.find((c) => c.id === contactId);
            if (!prevContact) return;

            const applyOptimistic = (up: Contact) => {
              if (up.id !== contactId) return up;
              const merged = { ...additionalFields, [field]: value };
              return { ...up, ...merged };
            };
            const revertOptimistic = () => {
              setRows((prev) => prev.map((c) => (c.id === contactId ? prevContact : c)));
            };

            const applyNoteOptimistic = (content: string | null, noteId?: string | null) => {
              setRows((prev) =>
                prev.map((c) =>
                  c.id === contactId ? { ...c, latest_note: content, latest_note_id: noteId ?? c.latest_note_id } : c
                )
              );
            };

            if (field === 'latest_note') {
              const latestNoteId = additionalFields?.latest_note_id;
              const noteContent = value && String(value).trim() ? value : null;
              const prevNote = prevContact.latest_note;
              applyNoteOptimistic(noteContent ?? '', latestNoteId || undefined);
              try {
                const { data: auth } = await supabase.auth.getUser();
                const uid = auth.user?.id;
                if (latestNoteId) {
                  const { error } = await supabase
                    .from('activities')
                    .update({ content: noteContent ?? '' })
                    .eq('id', latestNoteId)
                    .eq('org_id', orgId);
                  if (error) throw error;
                } else if (noteContent) {
                  const { data: insertData, error } = await supabase
                    .from('activities')
                    .insert([{
                      contact_id: contactId,
                      org_id: orgId,
                      type: 'note',
                      channel: 'web',
                      content: noteContent,
                      created_by: uid,
                    }])
                    .select('id')
                    .single();
                  if (error) throw error;
                  if (insertData?.id) {
                    applyNoteOptimistic(noteContent, insertData.id);
                  }
                } else {
                  setRows((prev) => prev.map((c) => (c.id === contactId ? prevContact : c)));
                }
              } catch (err: any) {
                setRows((prev) => prev.map((c) => (c.id === contactId ? prevContact : c)));
                toast.error(err?.message || 'Failed to save note');
              }
              return;
            }

            setRows((prev) => prev.map((c) => (c.id === contactId ? applyOptimistic(c) : c)));
            try {
              const updatePayload: any = { [field]: value };
              if (additionalFields) {
                Object.assign(updatePayload, additionalFields);
              }
              const { error } = await supabase
                .from('contacts')
                .update(updatePayload)
                .eq('id', contactId)
                .eq('org_id', orgId);
              if (error) throw error;
              if (field === 'prospect_status' && value === 'to_visit') {
                toast.success(t('crm.contacts.addedToNavigator') || 'Contatto aggiunto al Navigatore');
              }
            } catch (err: any) {
              revertOptimistic();
              toast.error(err?.message || 'Failed to save');
            }
          }}
          onUpdateLeadStatus={async (contactId, stage) => {
            if (!canEdit || !orgId) return;
            const prevStage = leadStages[contactId];
            setLeadStages((prev) => ({ ...prev, [contactId]: stage }));
            try {
              const { data: existingLead } = await supabase
                .from('leads')
                .select('id')
                .eq('contact_id', contactId)
                .eq('org_id', orgId)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();
              if (existingLead) {
                const { error } = await supabase
                  .from('leads')
                  .update({ stage })
                  .eq('id', existingLead.id)
                  .eq('org_id', orgId);
                if (error) throw error;
              } else {
                const contact = rows.find((c) => c.id === contactId);
                const title = contact?.company || contact?.full_name || 'Lead';
                const { error } = await supabase
                  .from('leads')
                  .insert([{ contact_id: contactId, org_id: orgId, title: `Lead: ${title}`, stage }]);
                if (error) throw error;
              }
            } catch (err: any) {
              setLeadStages((prev) => {
                const next = { ...prev };
                if (prevStage != null) next[contactId] = prevStage;
                else delete next[contactId];
                return next;
              });
              toast.error(err?.message || 'Failed to save lead status');
            }
          }}
          onEditContact={async (contact) => {
                              const contactToEdit = { 
              ...contact, 
              tags: contact.tags || [], 
              lifecycle_stage: contact.lifecycle_stage || 'contact',
              company: contact.company || null,
              company_address: contact.company_address || null,
              company_city: contact.company_city || null,
              company_zip: contact.company_zip || null,
              google_maps_url: contact.google_maps_url || null,
              website: contact.website || null,
              role: contact.role || null,
              prospect_status: contact.prospect_status || null,
              preferred_language: contact.preferred_language || null,
              route_index: contact.route_index || null,
              latest_note: contact.latest_note || null,
              latest_note_id: contact.latest_note_id || null,
                              };
                              setEditContact(contactToEdit);
                              
                              // If this is a lead, fetch the lead's current stage
                              if (contactToEdit.lifecycle_stage === 'lead' && !contactToEdit.prospect_status && orgId) {
                                const { data: leadData } = await supabase
                                  .from('leads')
                                  .select('stage')
                .eq('contact_id', contact.id)
                                  .eq('org_id', orgId)
                                  .order('created_at', { ascending: false })
                                  .limit(1)
                                  .maybeSingle();
                                
                                if (leadData?.stage && ['new', 'qualified', 'scheduled', 'won', 'lost'].includes(leadData.stage)) {
                                  setEditLeadStatus(leadData.stage as 'new' | 'qualified' | 'scheduled' | 'won' | 'lost');
                                } else {
                                  setEditLeadStatus('new');
                                }
                              } else {
                                setEditLeadStatus(null);
                              }
                              
                              setDrawer(true);
                            }}
          onDeleteContact={(contact) => {
            setConfirmDelete(contact);
          }}
          formatRelative={formatRelative}
          stageOptions={stageOptions}
          prospectStatusOptions={prospectStatusOptions}
          leadStatusOptions={leadStatusOptions}
          leadStages={leadStages}
          duplicateEmails={duplicateEmails}
          duplicatePhones={duplicatePhones}
          leadCounts={leadCounts}
          canEdit={canEdit}
          t={t}
        />
      )}

      {/* Pagination Controls */}
      {!loading && filteredCount > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0 mt-4 px-3 sm:px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
          <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 text-center sm:text-left">
            <span className="hidden sm:inline">{t('crm.contacts.pagination.showing')} </span>
            {startIndex + 1}-{Math.min(endIndex, filteredCount)} <span className="hidden sm:inline">{t('crm.contacts.pagination.of')} {filteredCount} {t('crm.contacts.pagination.contacts')}</span>
            <span className="sm:hidden"> / {filteredCount}</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 w-full sm:w-auto justify-center">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-2 sm:px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm font-medium min-w-[60px] sm:min-w-0"
            >
              <span className="hidden sm:inline">{t('crm.contacts.pagination.previous')}</span>
              <span className="sm:hidden">{t('crm.contacts.pagination.prevShort')}</span>
            </button>
            <div className="flex items-center gap-1 overflow-x-auto max-w-full sm:max-w-none">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-2 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                      currentPage === pageNum
                        ? 'bg-cyan-500 text-white'
                        : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-2 sm:px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm font-medium min-w-[60px] sm:min-w-0"
            >
              <span className="hidden sm:inline">{t('crm.contacts.pagination.next')}</span>
              <span className="sm:hidden">{t('crm.contacts.pagination.nextShort')}</span>
            </button>
          </div>
        </div>
      )}

      {drawer && canEdit && (
        <div className="fixed inset-0 bg-black/60 grid backdrop-blur-sm z-50">
          <div className="ml-auto h-full w-full lg:max-w-md bg-white dark:bg-gray-800 p-4 sm:p-6 overflow-auto border-l border-gray-200 dark:border-gray-700 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{editContact ? t('crm.contacts.drawer.editTitle') : t('crm.contacts.drawer.newTitle')}</h2>
              <button onClick={() => { setDrawer(false); setEditContact(null); setEditLeadStatus(null); }} className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100">{t('crm.common.buttons.close')}</button>
            </div>
            <div className="grid gap-3">
              <input
                placeholder={t('crm.contacts.drawer.fullName')}
                className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                value={editContact ? editContact.full_name || '' : newContact.full_name}
                onChange={e => editContact
                  ? setEditContact({ ...editContact, full_name: e.target.value })
                  : setNewContact({ ...newContact, full_name: e.target.value })}
              />
              <input
                placeholder={t('crm.contacts.drawer.email')}
                className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                value={editContact ? editContact.email || '' : newContact.email}
                onChange={e => editContact
                  ? setEditContact({ ...editContact, email: e.target.value })
                  : setNewContact({ ...newContact, email: e.target.value })}
              />
              <input
                placeholder={t('crm.contacts.drawer.phone')}
                className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                value={editContact ? editContact.phone || '' : newContact.phone}
                onChange={e => editContact
                  ? setEditContact({ ...editContact, phone: e.target.value })
                  : setNewContact({ ...newContact, phone: e.target.value })}
              />
              <input
                placeholder={t('crm.contacts.drawer.vat')}
                className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                value={editContact ? editContact.vat_number || '' : newContact.vat_number}
                onChange={e => editContact
                  ? setEditContact({ ...editContact, vat_number: e.target.value })
                  : setNewContact({ ...newContact, vat_number: e.target.value })}
              />
              <div>
                <input
                  placeholder={t('crm.contacts.drawer.source')}
                  className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                  value={editContact ? editContact.source || '' : newContact.source}
                  list="source-options"
                  onChange={e => editContact
                    ? setEditContact({ ...editContact, source: e.target.value })
                    : setNewContact({ ...newContact, source: e.target.value })}
                />
                <datalist id="source-options">
                  {sourceOptions.map((option) => (
                    <option key={option} value={option} />
                  ))}
                </datalist>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">{t('crm.contacts.drawer.rank')}</label>
                <select
                  className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 w-full focus:ring-cyan-500 focus:border-cyan-500"
                  value={editContact ? (editContact.client_rank != null ? String(editContact.client_rank) : '') : newContact.client_rank}
                  onChange={e => editContact
                    ? setEditContact({ ...editContact, client_rank: e.target.value ? Number(e.target.value) : null })
                    : setNewContact({ ...newContact, client_rank: e.target.value })}
                >
                  <option value="">{t('crm.contacts.drawer.rankPlaceholder')}</option>
                  {[1, 2, 3, 4, 5].map((val) => (
                    <option key={val} value={val}>{val}</option>
                  ))}
                </select>
              </div>
              {editContact && (() => {
                const currentType = editContact.prospect_status 
                  ? 'prospect' 
                  : (editContact.lifecycle_stage === 'lead' ? 'lead' 
                  : (editContact.lifecycle_stage === 'client' ? 'client' : 'contact'));
                return (
                  <>
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">{t('crm.contacts.drawer.typeLabel')}</label>
                    <select
                      className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 focus:ring-cyan-500 focus:border-cyan-500"
                      value={currentType}
                      onChange={async e => {
                        const type = e.target.value;
                        if (type === 'prospect') {
                          // If switching to prospect, set prospect_status if not already set
                          const newProspectStatus = editContact.prospect_status || 'new';
                          setEditContact({ 
                            ...editContact, 
                            lifecycle_stage: 'contact',
                            prospect_status: newProspectStatus
                          });
                          setEditLeadStatus(null);
                        } else if (type === 'lead') {
                          setEditContact({ 
                            ...editContact, 
                            lifecycle_stage: 'lead',
                            prospect_status: null
                          });
                          // If switching to lead and no status set, default to 'new'
                          // Also check if lead exists and fetch its stage
                          if (!editLeadStatus && orgId) {
                            const { data: leadData } = await supabase
                              .from('leads')
                              .select('stage')
                              .eq('contact_id', editContact.id)
                              .eq('org_id', orgId)
                              .order('created_at', { ascending: false })
                              .limit(1)
                              .maybeSingle();
                            
                            if (leadData?.stage && ['new', 'qualified', 'scheduled', 'won', 'lost'].includes(leadData.stage)) {
                              setEditLeadStatus(leadData.stage as 'new' | 'qualified' | 'scheduled' | 'won' | 'lost');
                            } else {
                              setEditLeadStatus('new');
                            }
                          }
                        } else if (type === 'client') {
                          setEditContact({ 
                            ...editContact, 
                            lifecycle_stage: 'client',
                            prospect_status: null
                          });
                          setEditLeadStatus(null);
                        } else {
                          setEditContact({ 
                            ...editContact, 
                            lifecycle_stage: 'contact',
                            prospect_status: null
                          });
                          setEditLeadStatus(null);
                        }
                      }}
                    >
                      <option value="contact">{t('crm.contacts.drawer.contactTypeOption')}</option>
                      <option value="lead">{t('crm.contacts.drawer.leadTypeOption')}</option>
                      <option value="client">{t('crm.common.stages.client')}</option>
                      <option value="prospect">Prospect</option>
                    </select>
                    {currentType === 'prospect' && (
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">{t('crm.contacts.drawer.prospectStatusLabel')}</label>
                        <select
                          className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 focus:ring-cyan-500 focus:border-cyan-500 w-full"
                          value={editContact.prospect_status || 'new'}
                          onChange={e => setEditContact({ ...editContact, prospect_status: e.target.value as ProspectStatus })}
                        >
                          {prospectStatusOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {currentType === 'lead' && (
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">{t('crm.contacts.drawer.leadStatusLabel')}</label>
                        <select
                          className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 focus:ring-cyan-500 focus:border-cyan-500 w-full"
                          value={editLeadStatus || 'new'}
                          onChange={e => setEditLeadStatus(e.target.value as 'new' | 'qualified' | 'scheduled' | 'won' | 'lost')}
                        >
                          <option value="new">{t('crm.leads.pipeline.headings.new')}</option>
                          <option value="qualified">{t('crm.leads.pipeline.headings.qualified')}</option>
                          <option value="scheduled">{t('crm.leads.pipeline.headings.scheduled')}</option>
                          <option value="won">{t('crm.leads.pipeline.headings.won')}</option>
                          <option value="lost">{t('crm.leads.pipeline.headings.lost')}</option>
                        </select>
                      </div>
                    )}
                  </>
                );
              })()}
              {!editContact && (
                <select
                  className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 focus:ring-cyan-500 focus:border-cyan-500"
                  value={newContact.stage}
                  onChange={e => setNewContact({ ...newContact, stage: e.target.value as 'contact' | 'lead' | 'client' })}
                >
                  {stageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
              <input
                placeholder={t('crm.contacts.drawer.tags')}
                className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                value={editContact ? (Array.isArray(editContact.tags) ? editContact.tags.join(', ') : '') : newContact.tags}
                onChange={e => {
                  const val = e.target.value;
                  if (editContact) {
                    setEditContact({ ...editContact, tags: val.split(',').map(s => s.trim()).filter(Boolean) });
                  } else {
                    setNewContact({ ...newContact, tags: val });
                  }
                }}
              />
              <input
                placeholder={t('crm.contacts.placeholderCompany')}
                className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                value={editContact ? editContact.company || '' : newContact.company}
                onChange={e => editContact
                  ? setEditContact({ ...editContact, company: e.target.value })
                  : setNewContact({ ...newContact, company: e.target.value })}
              />
              <input
                placeholder={t('crm.contacts.placeholderRole')}
                className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                value={editContact ? editContact.role || '' : newContact.role}
                onChange={e => editContact
                  ? setEditContact({ ...editContact, role: e.target.value })
                  : setNewContact({ ...newContact, role: e.target.value })}
              />
              <input
                placeholder={t('crm.detail.contact.preferredLanguagePlaceholder')}
                className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                value={editContact ? editContact.preferred_language || '' : newContact.preferred_language}
                onChange={e => editContact
                  ? setEditContact({ ...editContact, preferred_language: e.target.value })
                  : setNewContact({ ...newContact, preferred_language: e.target.value })}
              />
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">{t('crm.contacts.interestLevelLabel')}</label>
                <select
                  className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 w-full focus:ring-cyan-500 focus:border-cyan-500"
                  value={editContact ? (editContact.interest_level || '') : (newContact.interest_level || '')}
                  onChange={e => editContact
                    ? setEditContact({ ...editContact, interest_level: (e.target.value || null) as 'low' | 'medium' | 'high' | null })
                    : setNewContact({ ...newContact, interest_level: (e.target.value || '') as '' | 'low' | 'medium' | 'high' })}
                >
                  <option value="">—</option>
                  <option value="low">{t('crm.contacts.interestLevelLow')}</option>
                  <option value="medium">{t('crm.contacts.interestLevelMedium')}</option>
                  <option value="high">{t('crm.contacts.interestLevelHigh')}</option>
                </select>
              </div>
              <input
                placeholder={t('crm.contacts.routeIndexPlaceholder')}
                type="number"
                className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                value={editContact ? (editContact.route_index || '') : (newContact.route_index || '')}
                onChange={e => editContact
                  ? setEditContact({ ...editContact, route_index: e.target.value ? parseInt(e.target.value) : null })
                  : setNewContact({ ...newContact, route_index: e.target.value ? parseInt(e.target.value) : null })}
              />
              <input
                placeholder="Indirizzo Azienda / Company Address"
                className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                value={editContact ? editContact.company_address || '' : newContact.company_address}
                onChange={e => editContact
                  ? setEditContact({ ...editContact, company_address: e.target.value })
                  : setNewContact({ ...newContact, company_address: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  placeholder="Città / City"
                  className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                  value={editContact ? editContact.company_city || '' : newContact.company_city}
                  onChange={e => editContact
                    ? setEditContact({ ...editContact, company_city: e.target.value })
                    : setNewContact({ ...newContact, company_city: e.target.value })}
                />
                <input
                  placeholder="CAP / ZIP"
                  className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                  value={editContact ? editContact.company_zip || '' : newContact.company_zip}
                  onChange={e => editContact
                    ? setEditContact({ ...editContact, company_zip: e.target.value })
                    : setNewContact({ ...newContact, company_zip: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <div className="relative flex items-center gap-2">
                  <input
                    placeholder="Google Maps URL"
                    className="flex-1 bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 pr-10 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                    value={editContact ? editContact.google_maps_url || '' : newContact.google_maps_url}
                    onChange={async (e) => {
                      const url = e.target.value;
                      setMapsUrlExpanded(false);
                      setCoordinatesMessage({ type: null, text: '' });
                      
                      if (editContact) {
                        setEditContact({ ...editContact, google_maps_url: url });
                        
                        // Check if it's a short URL that needs expansion
                        if (url && url.length > 10 && isShortMapsUrl(url)) {
                          setMapsUrlExpanding(true);
                          try {
                            const response = await fetch('/api/utils/expand-maps-url', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ url })
                            });
                            const data = await response.json();
                            
                            if (data.success && data.lat && data.lng && orgId) {
                              // Update contact with expanded URL and coordinates
                              setEditContact({ 
                                ...editContact, 
                                google_maps_url: data.longUrl || url,
                                lat: data.lat,
                                lng: data.lng
                              });
                              // Save coordinates to database
                              await supabase
                                .from('contacts')
                                .update({ 
                                  lat: data.lat, 
                                  lng: data.lng,
                                  google_maps_url: data.longUrl || url
                                })
                                .eq('id', editContact.id)
                                .eq('org_id', orgId);
                              setMapsUrlExpanded(true);
                              setCoordinatesMessage({ 
                                type: 'success', 
                                text: `Posizione trovata: ${data.lat.toFixed(6)}, ${data.lng.toFixed(6)}` 
                              });
                              setTimeout(() => {
                                setMapsUrlExpanded(false);
                                setCoordinatesMessage({ type: null, text: '' });
                              }, 5000);
                            }
                          } catch (error) {
                            console.error('[Contacts] Error expanding URL:', error);
                          } finally {
                            setMapsUrlExpanding(false);
                          }
                        } else if (url && url.length > 20) {
                          // Auto-extract and save coordinates when URL is pasted (non-short URL)
                          const coords = extractCoordinatesFromUrl(url);
                          if (coords && orgId) {
                            setEditContact({
                              ...editContact,
                              google_maps_url: url,
                              lat: coords.lat,
                              lng: coords.lng
                            });
                            // Save coordinates to database
                            await supabase
                              .from('contacts')
                              .update({ lat: coords.lat, lng: coords.lng })
                              .eq('id', editContact.id)
                              .eq('org_id', orgId);
                            setCoordinatesMessage({ 
                              type: 'success', 
                              text: `Posizione trovata: ${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}` 
                            });
                            setTimeout(() => setCoordinatesMessage({ type: null, text: '' }), 5000);
                          }
                        }
                      } else {
                        setNewContact({ ...newContact, google_maps_url: url });
                        
                        // Check if it's a short URL that needs expansion
                        if (url && url.length > 10 && isShortMapsUrl(url)) {
                          setMapsUrlExpanding(true);
                          try {
                            const response = await fetch('/api/utils/expand-maps-url', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ url })
                            });
                            const data = await response.json();
                            
                            if (data.success && data.lat && data.lng) {
                              // Update form with expanded URL
                              setNewContact({ 
                                ...newContact, 
                                google_maps_url: data.longUrl || url 
                              });
                              setMapsUrlExpanded(true);
                              setCoordinatesMessage({ 
                                type: 'success', 
                                text: `Posizione trovata: ${data.lat.toFixed(6)}, ${data.lng.toFixed(6)}` 
                              });
                              setTimeout(() => {
                                setMapsUrlExpanded(false);
                                setCoordinatesMessage({ type: null, text: '' });
                              }, 5000);
                            }
                          } catch (error) {
                            console.error('[Contacts] Error expanding URL:', error);
                          } finally {
                            setMapsUrlExpanding(false);
                          }
                        }
                      }
                    }}
                    onBlur={async (e) => {
                      const url = e.target.value;
                      if (url && url.trim().length > 0) {
                        const currentMapsUrl = editContact ? editContact.google_maps_url : newContact.google_maps_url;
                        const currentLat = editContact ? editContact.lat : null;
                        const currentLng = editContact ? editContact.lng : null;
                        
                        // Auto-trigger only if lat/lng are empty
                        if ((!currentLat || !currentLng) && url === currentMapsUrl) {
                          await findCoordinates(url, !!editContact);
                        }
                      }
                    }}
                  />
                  {/* Find Coordinates Button */}
                  {(editContact ? editContact.google_maps_url : newContact.google_maps_url) && (
                    <button
                      type="button"
                      onClick={() => {
                        const url = editContact ? editContact.google_maps_url : newContact.google_maps_url;
                        if (url) {
                          findCoordinates(url, !!editContact);
                        }
                      }}
                      disabled={mapsUrlExpanding}
                      className="flex-shrink-0 p-2 rounded-lg border border-cyan-500 dark:border-cyan-400 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-100 dark:hover:bg-cyan-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Trova Coordinate"
                    >
                      {mapsUrlExpanding ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
                {/* Success/Error Message */}
                {coordinatesMessage.type && (
                  <div className={`text-xs px-2 py-1 rounded ${
                    coordinatesMessage.type === 'success' 
                      ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400' 
                      : 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                  }`}>
                    {coordinatesMessage.text}
                  </div>
                )}
                {/* Legacy success indicator (if still needed) */}
                {mapsUrlExpanded && !mapsUrlExpanding && !coordinatesMessage.type && (
                  <div className="text-xs text-green-600 dark:text-green-400">
                    📍 Coordinate trovate
                  </div>
                )}
              </div>
              <input
                placeholder="Website URL"
                className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                value={editContact ? editContact.website || '' : newContact.website}
                onChange={e => editContact
                  ? setEditContact({ ...editContact, website: e.target.value })
                  : setNewContact({ ...newContact, website: e.target.value })}
              />
              <textarea
                placeholder="Note / Notes"
                className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500 min-h-[80px]"
                value={editContact ? editContact.latest_note || '' : newContact.latest_note || ''}
                onChange={e => editContact
                  ? setEditContact({ ...editContact, latest_note: e.target.value })
                  : setNewContact({ ...newContact, latest_note: e.target.value })}
              />
              <div className="flex justify-end gap-3 mt-4">
                <button onClick={() => { setDrawer(false); setEditContact(null); setEditLeadStatus(null); }} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">{t('crm.common.buttons.cancel')}</button>
                {editContact ? (
                  <button onClick={() => updateContact(editContact.id)} className="px-4 py-2 rounded-lg bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors shadow-md">{t('crm.common.buttons.save')}</button>
                ) : (
                  <button onClick={createContact} className="px-4 py-2 rounded-lg bg-cyan-500 text-white font-semibold hover:bg-cyan-600 transition-colors shadow-md">{t('crm.common.buttons.create')}</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && canEdit && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('crm.contacts.delete.title')}</h3>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t('crm.contacts.delete.message', { name: confirmDelete.full_name || confirmDelete.email })}
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">{t('crm.contacts.delete.cancel')}</button>
              <button onClick={() => deleteContact(confirmDelete.id)} className="px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 transition-colors shadow-md">{t('crm.contacts.delete.confirm')}</button>
            </div>
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center backdrop-blur-sm p-4 z-50">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('crm.contacts.importListTitle')}</h3>
              <button onClick={() => setShowImportModal(false)} className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100">✕</button>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              {t('crm.contacts.importModal.uploadHint')}
              <br />
              <span className="font-semibold">Google Maps</span>, 
              <span className="font-semibold"> Ragione Sociale</span>, 
              <span className="font-semibold"> Rating</span>, 
              <span className="font-semibold"> Rating#</span>, 
              <span className="font-semibold"> Categoria Business</span>, 
              <span className="font-semibold"> Indirizzo</span>, 
              <span className="font-semibold"> Orario</span>, 
              <span className="font-semibold"> Telefono</span>, 
              <span className="font-semibold"> Website</span>
            </p>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleFileImport(file);
                }
              }}
              className="w-full mb-4 text-sm text-gray-700 dark:text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-cyan-500 file:text-white file:hover:bg-cyan-600 file:cursor-pointer cursor-pointer"
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowImportModal(false)} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                {t('crm.common.buttons.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsPage;


